import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Logger, LoggerFactory } from '../../core/logger.service';
import {
  ConsentEvent,
  FlyoutDataType,
  FlyoutOpeningData,
  PersonDetails,
  SessionDetails,
  Visit,
  VisitDevice,
  VisitGoal,
} from '../flyout-models';
import { Subscription } from 'rxjs';
import { FlyoutService } from '../flyout.service';
import { AccountService } from 'app/account/account.service';
import { Account } from 'app/back-office/account.model';

@Component({
  selector: 'app-flyout-general-info',
  templateUrl: './flyout-general-info.component.html',
  styleUrls: ['./flyout-general-info.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutGeneralInfoComponent implements OnInit, OnDestroy {
  private readonly logger: Logger;
  private _openingData: FlyoutOpeningData;
  private currentAccount: Account;
  public personDetails: PersonDetails;
  private personSubscription: Subscription;
  private sessionSubscription: Subscription;
  private subscriptionArray: Subscription[] = [];
  public sessionDetails: SessionDetails;
  public FlyoutDataTypes = FlyoutDataType;
  public personVisits: number;
  public totalVisits: number;
  public isIsp: boolean;
  public visitsTimespan: number;

  public isConsentGiven: undefined;
  public devices: Array<VisitDevice>;
  public visits: Array<Visit>;
  public previousVisits: number;
  public uniqueVisitors: undefined;
  public consentEvents: Array<ConsentEvent>;
  public hasMobileDevice = false;
  public hasDesktopDevice = false;
  public countryCode: string;
  public geoLocation: string;
  public organizationName: string;
  public isSession = false;
  public browserType: string;
  public osType: string;
  public nativeAppPlatform: string;
  public expandedTab: string;
  public sessionGoals?: Array<VisitGoal>;
  public profileGoals?: Array<VisitGoal>;
  public totalSales?: number;
  public totalSalesVisits: number;
  public currentAccountCurrency?: string;

  @Output() public onClickToScroll = new EventEmitter<string>();

  @Input() public set flyoutOpeningData(openingData: FlyoutOpeningData) {
    this._openingData = openingData;
    this.expandedTab = this._openingData.expandedTab;
    this.processStatistics();
  }

  public get flyoutOpeningData(): FlyoutOpeningData {
    return this._openingData;
  }

  constructor(
    public readonly loggerFactory: LoggerFactory,
    private readonly flyoutService: FlyoutService,
    private readonly accountService: AccountService,
  ) {
    this.logger = loggerFactory.get('FlyoutGeneralInfoComponent');
  }

  async ngOnInit() {
    this.currentAccount = await this.accountService.getCurrentAccountAsync();
    this.currentAccountCurrency = this.currentAccount.currency?.toUpperCase();
    this.personSubscription = this.flyoutService.personDetailsBS.subscribe((personDetails: PersonDetails) => {
      this.personDetails = personDetails;
      this.profileGoals = personDetails?.goals;
      this.totalSales = this.personDetails?.revenue || 0;
      this.processStatistics();
    });

    this.sessionSubscription = this.flyoutService.sessionDetailsBS.subscribe((sessionDetails: SessionDetails) => {
      this.sessionDetails = sessionDetails;
      this.totalSalesVisits = sessionDetails?.goals?.reduce((sales, goal) => sales + Number(goal.revenue), 0);
      this.processStatistics();
    });
  }

  public clickToScroll(elementId: string) {
    this.onClickToScroll.emit(elementId);
  }

  public openDetailsByUid() {
    this.flyoutService.open({
      type: FlyoutDataType.Contact,
      uid: this.sessionDetails.uid,
      siteId: this._openingData?.siteId,
      widgets: this.flyoutService.getWidgetsList(),
    } as FlyoutOpeningData);
  }

  private processStatistics() {
    if (!this._openingData) {
      return;
    }
    let devices = undefined,
      visits = undefined,
      previousVisits = undefined,
      consentEvents = undefined,
      hasMobileDevice = false,
      hasDesktopDevice = false,
      countryCode = undefined,
      geoLocation = undefined,
      uniqueVisitors = undefined,
      organizationName = undefined;
    this.isSession = this.flyoutOpeningData.type === FlyoutDataType.Session;
    if (this._openingData.type === FlyoutDataType.Contact && this.personDetails) {
      devices = this.personDetails.devices;
      visits = this.personDetails.visits;
      previousVisits = this.personDetails.previousVisits;
      consentEvents = this.personDetails.consents;
      hasMobileDevice = this.checkIfDeviceExists(devices, 'mobile');
      hasDesktopDevice = this.checkIfDeviceExists(devices, 'desktop');
      uniqueVisitors = this.personDetails.uniqueVisitors;
    } else if (this._openingData.type === FlyoutDataType.Session && this.sessionDetails) {
      const device = {
        operatingSystem: this.sessionDetails.operatingSystem,
        screenResolution: this.sessionDetails.screenResolution,
        browser: this.sessionDetails.webBrowser,
      } as VisitDevice;
      devices = [device];
      consentEvents = this.sessionDetails.consents;
      hasMobileDevice = this.checkIfDeviceExists(devices, 'mobile');
      hasDesktopDevice = this.checkIfDeviceExists(devices, 'desktop');
      countryCode = this.sessionDetails.countryCode
        ? this.sessionDetails.countryCode
        : this.flyoutOpeningData.countryCode;
      geoLocation = this.sessionDetails.geoLocation;
      // converting visitsTimeToLive from seconds to days
      if (this.currentAccount && this.currentAccount.visitsTimeToLive) {
        this.visitsTimespan = this.currentAccount.visitsTimeToLive / 86400;
      } else {
        this.visitsTimespan = undefined;
      }

      if (
        this.sessionDetails.netName &&
        (!this.sessionDetails.personalData || !this.sessionDetails.personalData.email)
      ) {
        const ispTagExists = this.sessionDetails.tags && this.sessionDetails.tags.indexOf('Isp') !== -1;
        if (!ispTagExists) {
          organizationName = this.sessionDetails.netName;
        } else {
          this.isIsp = true;
        }
      }

      if (!this.sessionDetails.personalData?.email && this.personDetails) {
        this.personVisits = this.personDetails.previousVisits ?? this.personDetails?.visits?.length;
      }
      this.browserType = this.parseBrowserType(this.sessionDetails.webBrowser);
      this.osType = this.parseOsType(this.sessionDetails.operatingSystem);
      this.nativeAppPlatform = this.detectNativePlatform(
        this.sessionDetails.operatingSystem,
        this.sessionDetails.webBrowser,
      );
    }

    this.devices = devices;
    this.hasMobileDevice = hasMobileDevice;
    this.hasDesktopDevice = hasDesktopDevice;
    this.visits = visits;
    this.previousVisits = previousVisits;
    this.countryCode = countryCode;
    this.geoLocation = geoLocation;
    this.consentEvents = consentEvents;
    this.uniqueVisitors = uniqueVisitors;
    this.organizationName = organizationName;
  }

  private checkIfDeviceExists(devices: Array<VisitDevice>, type: string) {
    if (!devices || devices.length === 0) {
      return false;
    }
    for (let i = 0; i < devices.length; i++) {
      if (devices[i].screenResolution) {
        const resolutionParts = devices[i].screenResolution.toLowerCase().split('x');
        const width = Number(resolutionParts[0]);
        if (type === 'mobile' && width <= 676) {
          return true;
        }
        if (type === 'desktop' && width > 676) {
          return true;
        }
        return false;
      } else {
        return false;
      }
    }
  }

  // Native-app traffic has no real browser, so Tracker stores "Other" (or empty) for browser.
  private detectNativePlatform(operatingSystem: string, webBrowser: string): string | undefined {
    if (!operatingSystem) {
      return undefined;
    }
    const browser = (webBrowser || '').toLowerCase();
    if (browser && browser !== 'other') {
      return undefined;
    }
    const os = operatingSystem.toLowerCase();
    if (os.indexOf('android') !== -1) {
      return 'Android';
    }
    if (os.indexOf('ios') !== -1 || os.indexOf('iphone') !== -1 || os.indexOf('ipad') !== -1) {
      return 'iOS';
    }
    return undefined;
  }

  private parseBrowserType(webBrowser: string): string {
    if (!webBrowser) {
      return undefined;
    }
    webBrowser = webBrowser.toLowerCase();
    if (webBrowser.indexOf('chrome') !== -1) {
      return 'chrome';
    }
    if (webBrowser.indexOf('explorer') !== -1) {
      return 'explorer';
    }
    if (webBrowser.indexOf('edge') !== -1) {
      return 'edge';
    }
    if (webBrowser.indexOf('safari') !== -1) {
      return 'safari';
    }
    if (webBrowser.indexOf('firefox') !== -1) {
      return 'firefox';
    }
    if (webBrowser.indexOf('opera') !== -1) {
      return 'opera';
    }
    if (webBrowser.indexOf('blackberry') !== -1) {
      return 'blackberry';
    }
    return undefined;
  }

  private parseOsType(operatingSystem: string): string {
    if (!operatingSystem) {
      return undefined;
    }
    if (operatingSystem.toLowerCase().indexOf('windows') !== -1) {
      return 'windows';
    }
    if (
      operatingSystem.toLowerCase().indexOf('mac') !== -1 ||
      operatingSystem.toLowerCase().indexOf('iphone') !== -1 ||
      operatingSystem.toLowerCase().indexOf('ios') !== -1 ||
      operatingSystem.toLowerCase().indexOf('os x') !== -1
    ) {
      return 'apple';
    }
    if (operatingSystem.toLowerCase().indexOf('linux') !== -1) {
      return 'linux';
    }
    if (operatingSystem.toLowerCase().indexOf('android') !== -1) {
      return 'android';
    }
    if (operatingSystem.toLowerCase().indexOf('blackberry') !== -1) {
      return 'blackberry';
    }
    return undefined;
  }

  ngOnDestroy(): void {
    this.subscriptionArray.push(this.personSubscription, this.sessionSubscription);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }
}
